import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { BOOKING_URL, IMAGES } from "../data/content";
import { useT } from "../i18n/LanguageContext";

// Masked line-by-line reveal helper
const revealLine = {
  initial: { y: "110%", opacity: 0 },
  animate: { y: "0%", opacity: 1 },
};

export default function Hero() {
  const { t, lang } = useT();

  const titleLines = [t("hero.title_1"), t("hero.title_2"), t("hero.title_3"), t("hero.title_4")];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      {/* Parallax background */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.15 }}
        animate={{ scale: 1.05 }}
        transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <img
          src={IMAGES.hero}
          alt="Hair & Beauty Nava"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      </motion.div>

      <div className="absolute top-1/2 right-8 hidden lg:flex flex-col items-center gap-3 z-10">
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-[#D4AF37]/60 to-transparent" />
        <span className="rotate-90 origin-center text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]/70 whitespace-nowrap">
          {t("hero.est")}
        </span>
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-[#D4AF37]/60 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-40 pb-24 min-h-screen flex flex-col justify-end">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
            data-testid="hero-overline"
          >
            <Sparkles size={14} className="text-[#D4AF37]" strokeWidth={1.5} />
            <span className="overline">{t("hero.tagline")}</span>
          </motion.div>

          <h1
            className="font-serif-display text-[13vw] sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.95] text-white font-light tracking-tight"
            data-testid="hero-title"
            key={lang}
          >
            {titleLines.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  variants={revealLine}
                  initial="initial"
                  animate="animate"
                  transition={{
                    duration: 0.9,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.3 + i * 0.12,
                  }}
                >
                  {i === 2 ? (
                    <em className="shimmer-gold not-italic font-normal">{line}</em>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed font-light"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-btn"
              className="btn-gold group"
            >
              {t("hero.cta_book")}
              <ArrowRight
                size={16}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
            <a href="#leistungen" data-testid="hero-services-btn" className="btn-ghost-gold">
              {t("hero.cta_services")}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.4 }}
          className="mt-20 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-6 sm:gap-12 text-[11px] tracking-[0.28em] uppercase text-white/50"
          data-testid="hero-meta"
        >
          <span><span className="text-[#D4AF37]">01 ·</span> {t("hero.meta_1")}</span>
          <span><span className="text-[#D4AF37]">02 ·</span> {t("hero.meta_2")}</span>
          <span><span className="text-[#D4AF37]">03 ·</span> {t("hero.meta_3")}</span>
        </motion.div>
      </div>
    </section>
  );
}
